import React, { useState, useEffect, useCallback } from 'react';
import { Container, Card, Row, Col, Button, Form, Modal, Badge, Spinner, Alert } from 'react-bootstrap';
import { Plus, Edit2, Trash2, Music, Calendar, ChevronDown, ChevronRight, X, Save } from 'lucide-react';
import Navigation from '../components/Navigation/Navigation';
import { useConfirmModal } from '../components/common/ConfirmModal';
import apiClient from '../utils/apiClient';
import { useChurch } from '../contexts/ChurchContext';

interface PerformanceType {
  id: number;
  name: string;
}

interface AnthemIdea {
  id: number;
  season: string;
  anthem_title: string;
  composer?: string;
  arranger?: string;
  notes?: string;
  voicing?: string;
  song_id?: number;
  song_title?: string;
  performance_type_id?: number;
  performance_type_name?: string;
  target_date?: string; // 'YYYY-MM-DD' — Sunday penciled in for (optional)
  created_at: string;
  updated_at: string;
}

interface PerformedAnthem {
  service_id: number;
  liturgy_date: string;
  liturgy_name: string;
  season: string;
  anthem_title: string;
  composer?: string;
  performance_type_name?: string;
}

interface SeasonData {
  seasons: string[];
  ideas: Record<string, AnthemIdea[]>;
  performed: Record<string, PerformedAnthem[]>;
}

interface ProgramYearService {
  service_id: number;
  liturgy_date: string;
  liturgy_name: string;
  season: string;
  has_anthem: boolean;
  anthem_title?: string;
}

interface SongResult {
  song_id: number;
  title: string;
  composer_name?: string;
  versions: { version_id: number; is_default?: boolean }[];
}

// A past anthem (idea or sung) offered for reuse into the current year.
interface AnthemHistoryItem {
  anthem_title: string;
  composer?: string;
  arranger?: string;
  voicing?: string;
  performance_type_id?: number;
  performance_type_name?: string;
  season: string;
  song_id?: number;
  program_year?: string;
  source: 'idea' | 'sung';
}

const SEASON_COLORS: Record<string, string> = {
  'Advent': '#6B21A8',
  'Christmas': '#B45309',
  'Epiphany': '#047857',
  'Lent': '#7C2D12',
  'Holy Week': '#991B1B',
  'Easter': '#FBBF24',
  'Pentecost - Summer': '#059669',
  'Pentecost - Fall': '#DC2626',
};

const SEASON_DESCRIPTIONS: Record<string, string> = {
  'Advent': 'Preparation and anticipation for Christ\'s coming',
  'Christmas': 'Celebration of the Incarnation',
  'Epiphany': 'Manifestation of Christ to the world',
  'Lent': 'Penitence and preparation for Easter',
  'Holy Week': 'Passion of Christ — Palm Sunday through Holy Saturday',
  'Easter': 'Celebration of the Resurrection',
  'Pentecost - Summer': 'Ordinary time - growth in faith',
  'Pentecost - Fall': 'Ordinary time - harvest and thanksgiving',
};

// A short "Oct 12" style label from a 'YYYY-MM-DD' date (no timezone shift).
const shortDate = (ymd: string): string =>
  new Date(ymd + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' });

const SeasonalAnthemPlanningPage: React.FC = () => {
  const { currentChurch } = useChurch();
  const { confirm, ConfirmModal } = useConfirmModal();
  const [seasonData, setSeasonData] = useState<SeasonData>({ seasons: [], ideas: {}, performed: {} });
  const [performanceTypes, setPerformanceTypes] = useState<PerformanceType[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedSeasons, setExpandedSeasons] = useState<Set<string>>(new Set());

  // Program-year picker (Sept–Aug choir seasons)
  const [programYears, setProgramYears] = useState<string[]>([]);
  const [selectedYear, setSelectedYear] = useState<string>('');

  // The Sundays in the selected year — powers the "pencil in for" dropdown and
  // the "add for real" write. Loaded whenever the year changes.
  const [yearServices, setYearServices] = useState<ProgramYearService[]>([]);

  // Pool of past anthems (all years) to reuse into the current year.
  const [anthemHistory, setAnthemHistory] = useState<AnthemHistoryItem[]>([]);

  // Add/Edit idea modal
  const [showModal, setShowModal] = useState(false);
  const [editingIdea, setEditingIdea] = useState<AnthemIdea | null>(null);
  const [modalSeason, setModalSeason] = useState<string>('');
  const [formData, setFormData] = useState({
    anthem_title: '',
    composer: '',
    arranger: '',
    voicing: '',
    notes: '',
    performance_type_id: 0,
    target_date: '',
    song_id: null as number | null
  });
  const [saving, setSaving] = useState(false);

  // Reuse-a-past-anthem search (inside the Add modal)
  const [reuseTerm, setReuseTerm] = useState('');
  const [reuseOpen, setReuseOpen] = useState(false);

  // "Also add it for real" — commit to a real Sunday as the offertory anthem.
  const [addForReal, setAddForReal] = useState(false);
  const [songTerm, setSongTerm] = useState('');
  const [songResults, setSongResults] = useState<SongResult[]>([]);
  const [pickedSong, setPickedSong] = useState<{ song_id: number; title: string; composer_name?: string; version_id: number | null } | null>(null);

  // The program year containing a given date (Sept–Aug), e.g. "2025-2026".
  const programYearOf = (d: Date): string => {
    const y = d.getFullYear();
    return d.getMonth() >= 8 ? `${y}-${y + 1}` : `${y - 1}-${y}`;
  };

  // Load the list of program years (from the server) plus the current and next
  // planning years, so an empty upcoming year can still be selected to plan.
  const loadYears = useCallback(async () => {
    try {
      const params = currentChurch ? `?churchId=${currentChurch.id}` : '';
      const res = await apiClient.get(`/seasonal-anthem-ideas/years${params}`);
      const serverYears: string[] = res.data?.data || [];

      const now = new Date();
      const current = programYearOf(now);
      const nextStart = parseInt(current.slice(0, 4), 10) + 1;
      const next = `${nextStart}-${nextStart + 1}`;

      const merged = Array.from(new Set([...serverYears, current, next]))
        .sort((a, b) => b.localeCompare(a)); // most recent first

      setProgramYears(merged);
      setSelectedYear(prev =>
        prev && merged.includes(prev)
          ? prev
          : (merged.includes(current) ? current : merged[0] || current)
      );
    } catch (err) {
      console.error('Failed to load program years', err);
    }
  }, [currentChurch]);

  const fetchData = useCallback(async () => {
    if (!selectedYear) return;
    try {
      setLoading(true);
      const churchParam = currentChurch ? `&churchId=${currentChurch.id}` : '';

      // Ideas + performed (for the year), performance types, the year's Sundays,
      // and the reuse pool (all other years) in parallel.
      const [ideasRes, typesRes, servicesRes, historyRes] = await Promise.all([
        apiClient.get(`/seasonal-anthem-ideas?programYear=${encodeURIComponent(selectedYear)}${churchParam}`),
        apiClient.get('/performance-types'),
        apiClient.get(`/seasonal-anthem-ideas/services?programYear=${encodeURIComponent(selectedYear)}${churchParam}`),
        apiClient.get(`/seasonal-anthem-ideas/history?excludeYear=${encodeURIComponent(selectedYear)}${churchParam}`)
      ]);

      if (ideasRes.data.success) {
        setSeasonData(ideasRes.data.data);
        setExpandedSeasons(new Set(ideasRes.data.data.seasons));
      }

      setPerformanceTypes(typesRes.data || []);
      setYearServices(servicesRes.data?.data || []);
      setAnthemHistory(historyRes.data?.data || []);
    } catch (err) {
      setError('Failed to load seasonal anthem ideas');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [currentChurch, selectedYear]);

  useEffect(() => {
    loadYears();
  }, [loadYears]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const toggleSeason = (season: string) => {
    setExpandedSeasons(prev => {
      const newSet = new Set(prev);
      if (newSet.has(season)) {
        newSet.delete(season);
      } else {
        newSet.add(season);
      }
      return newSet;
    });
  };

  // Sundays that fall in a given season (for the "pencil in for" dropdown).
  const sundaysForSeason = (season: string): ProgramYearService[] =>
    yearServices.filter(s => s.season === season);

  const resetModalExtras = () => {
    setReuseTerm('');
    setReuseOpen(false);
    setAddForReal(false);
    setSongTerm('');
    setSongResults([]);
    setPickedSong(null);
  };

  const openAddModal = (season: string, performanceTypeId?: number) => {
    setModalSeason(season);
    setEditingIdea(null);
    setFormData({
      anthem_title: '',
      composer: '',
      arranger: '',
      voicing: '',
      notes: '',
      performance_type_id: performanceTypeId || (performanceTypes[0]?.id || 0),
      target_date: '',
      song_id: null
    });
    resetModalExtras();
    setShowModal(true);
  };

  const openEditModal = (idea: AnthemIdea) => {
    setModalSeason(idea.season);
    setEditingIdea(idea);
    setFormData({
      anthem_title: idea.anthem_title,
      composer: idea.composer || '',
      arranger: idea.arranger || '',
      voicing: idea.voicing || '',
      notes: idea.notes || '',
      performance_type_id: idea.performance_type_id || 0,
      target_date: idea.target_date || '',
      song_id: idea.song_id ?? null
    });
    resetModalExtras();
    // If the idea is already linked to a library song, seed it for "add for real".
    if (idea.song_id) {
      setPickedSong({ song_id: idea.song_id, title: idea.song_title || idea.anthem_title, version_id: null });
    }
    setShowModal(true);
  };

  // Reuse a past anthem: fill the form from it and drop into its season.
  const applyReuse = (item: AnthemHistoryItem) => {
    // Match a performance type by name when the source (a sung anthem) has no id.
    let typeId = item.performance_type_id || 0;
    if (!typeId && item.performance_type_name) {
      const match = performanceTypes.find(
        pt => pt.name.toLowerCase() === item.performance_type_name!.toLowerCase()
      );
      if (match) typeId = match.id;
    }
    setModalSeason(item.season);
    setFormData(prev => ({
      ...prev,
      anthem_title: item.anthem_title,
      composer: item.composer || '',
      arranger: item.arranger || '',
      voicing: item.voicing || '',
      performance_type_id: typeId || prev.performance_type_id,
      target_date: '', // dates don't carry across years — pencil a new one
      song_id: item.song_id ?? null
    }));
    if (item.song_id) {
      setPickedSong({ song_id: item.song_id, title: item.anthem_title, composer_name: item.composer, version_id: null });
    } else {
      setPickedSong(null);
    }
    setReuseTerm(item.anthem_title);
    setReuseOpen(false);
  };

  const reuseMatches = (): AnthemHistoryItem[] => {
    const t = reuseTerm.trim().toLowerCase();
    const pool = t
      ? anthemHistory.filter(a =>
          a.anthem_title.toLowerCase().includes(t) ||
          (a.composer || '').toLowerCase().includes(t))
      : anthemHistory;
    return pool.slice(0, 10);
  };

  const searchSongs = async (term: string) => {
    setSongTerm(term);
    setPickedSong(null);
    setFormData(prev => ({ ...prev, song_id: null }));
    if (term.trim().length < 2) {
      setSongResults([]);
      return;
    }
    try {
      const res = await apiClient.get(`/song-selections/search?term=${encodeURIComponent(term.trim())}&limit=10`);
      setSongResults(res.data?.songs || []);
    } catch (err) {
      console.error('Song search failed', err);
      setSongResults([]);
    }
  };

  const pickSong = (s: SongResult) => {
    const def = s.versions?.find(v => v.is_default) || s.versions?.[0];
    setPickedSong({ song_id: s.song_id, title: s.title, composer_name: s.composer_name, version_id: def?.version_id ?? null });
    setFormData(prev => ({ ...prev, song_id: s.song_id }));
    setSongResults([]);
    setSongTerm(s.title);
  };

  // "Add for real" is only valid once a Sunday is chosen and a library song is
  // linked (a real song is required to become the actual offertory anthem).
  const realReady = !addForReal || (!!formData.target_date && !!pickedSong);

  const handleSave = async () => {
    if (!formData.anthem_title.trim() || !formData.performance_type_id || !realReady) {
      return;
    }

    try {
      setSaving(true);

      const payload = {
        anthem_title: formData.anthem_title,
        composer: formData.composer,
        arranger: formData.arranger,
        voicing: formData.voicing,
        notes: formData.notes,
        performance_type_id: formData.performance_type_id,
        song_id: formData.song_id,
        target_date: formData.target_date || null,
        season: modalSeason,
        program_year: selectedYear,
        church_id: currentChurch?.id
      };

      if (editingIdea) {
        await apiClient.put(`/seasonal-anthem-ideas/${editingIdea.id}`, payload);
      } else {
        await apiClient.post('/seasonal-anthem-ideas', payload);
      }

      // Also put it on the schedule for real, if asked.
      if (addForReal && formData.target_date && pickedSong) {
        const service = yearServices.find(s => s.liturgy_date === formData.target_date);
        if (service) {
          await apiClient.post('/seasonal-anthem-ideas/performed', {
            service_id: service.service_id,
            song_id: pickedSong.song_id,
            version_id: pickedSong.version_id
          });
        }
      }

      setShowModal(false);
      fetchData();
    } catch (err) {
      console.error('Error saving anthem idea:', err);
      setError('Failed to save anthem idea');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = (id: number, title: string) => {
    confirm({
      title: 'Delete Idea',
      message: `Are you sure you want to delete "${title}"?`,
      confirmText: 'Delete',
      variant: 'danger',
      onConfirm: async () => {
        try {
          await apiClient.delete(`/seasonal-anthem-ideas/${id}`);
          fetchData();
        } catch (err) {
          console.error('Error deleting anthem idea:', err);
          setError('Failed to delete anthem idea');
        }
      }
    });
  };

  const getSeasonColor = (season: string): string => {
    return SEASON_COLORS[season] || '#6B7280';
  };

  const getTypesWithIdeas = (ideas: AnthemIdea[]): (PerformanceType | { id: 0; name: string })[] => {
    const typeIds = new Set(ideas.map(i => i.performance_type_id || 0));
    const types: (PerformanceType | { id: 0; name: string })[] = [];

    performanceTypes.forEach(pt => {
      if (typeIds.has(pt.id)) {
        types.push(pt);
      }
    });

    if (typeIds.has(0)) {
      types.push({ id: 0, name: 'Uncategorized' });
    }

    return types;
  };

  const groupIdeasByType = (ideas: AnthemIdea[]): Record<number, AnthemIdea[]> => {
    const grouped: Record<number, AnthemIdea[]> = {};
    ideas.forEach(idea => {
      const typeId = idea.performance_type_id || 0;
      if (!grouped[typeId]) {
        grouped[typeId] = [];
      }
      grouped[typeId].push(idea);
    });
    return grouped;
  };

  if (loading) {
    return (
      <>
        <Navigation />
        <Container className="mt-4 text-center">
          <Spinner animation="border" role="status">
            <span className="visually-hidden">Loading...</span>
          </Spinner>
        </Container>
      </>
    );
  }

  return (
    <>
      <Navigation />
      <Container className="mt-4 mb-5">
        <div className="text-center mb-4">
          <h1 className="display-5 mb-2">
            <Calendar className="me-3" size={36} />
            Seasonal Planning
          </h1>
          <p className="lead text-muted">
            Anthems by liturgical season — what we sang, and what we're planning
          </p>
          <div className="d-inline-flex align-items-center gap-2 mt-2">
            <span className="text-muted small">Choir year</span>
            <Form.Select
              value={selectedYear}
              onChange={(e) => setSelectedYear(e.target.value)}
              style={{ width: 'auto' }}
              aria-label="Choir program year"
            >
              {programYears.map(y => (
                <option key={y} value={y}>{y}</option>
              ))}
            </Form.Select>
          </div>
        </div>

        {error && (
          <Alert variant="danger" dismissible onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <Row>
          {seasonData.seasons.map(season => {
            const ideas = seasonData.ideas[season] || [];
            const performed = seasonData.performed?.[season] || [];
            const isExpanded = expandedSeasons.has(season);
            const seasonColor = getSeasonColor(season);
            const typesWithIdeas = getTypesWithIdeas(ideas);
            const ideasByType = groupIdeasByType(ideas);

            return (
              <Col key={season} xs={12} className="mb-3">
                <Card className="shadow-sm">
                  <Card.Header
                    className="d-flex justify-content-between align-items-center"
                    style={{
                      background: `linear-gradient(135deg, ${seasonColor}22 0%, ${seasonColor}11 100%)`,
                      borderLeft: `4px solid ${seasonColor}`,
                      cursor: 'pointer'
                    }}
                    onClick={() => toggleSeason(season)}
                  >
                    <div className="d-flex align-items-center">
                      {isExpanded ? <ChevronDown size={20} /> : <ChevronRight size={20} />}
                      <h5 className="mb-0 ms-2" style={{ color: seasonColor }}>
                        {season}
                      </h5>
                      {performed.length > 0 && (
                        <Badge bg="dark" className="ms-2">
                          {performed.length} sung
                        </Badge>
                      )}
                      <Badge
                        bg="secondary"
                        className="ms-2"
                        style={{ backgroundColor: seasonColor }}
                      >
                        {ideas.length} {ideas.length === 1 ? 'idea' : 'ideas'}
                      </Badge>
                    </div>
                    <Button
                      variant="outline-primary"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        openAddModal(season);
                      }}
                    >
                      <Plus size={16} className="me-1" />
                      Add Idea
                    </Button>
                  </Card.Header>

                  {isExpanded && (
                    <Card.Body>
                      <p className="text-muted small mb-3">
                        {SEASON_DESCRIPTIONS[season]}
                      </p>

                      {performed.length > 0 && (
                        <div className="mb-4">
                          <h6 className="text-uppercase text-muted mb-2" style={{ fontSize: '0.75rem', letterSpacing: '0.05em' }}>
                            <Music size={14} className="me-1" />
                            Sung this year
                          </h6>
                          <div className="performed-list">
                            {performed.map(a => (
                              <div
                                key={a.service_id}
                                className="d-flex align-items-baseline py-1 border-bottom"
                                style={{ fontSize: '0.9rem' }}
                              >
                                <span className="text-muted me-3" style={{ minWidth: 92, fontVariantNumeric: 'tabular-nums' }}>
                                  {shortDate(a.liturgy_date)}
                                </span>
                                <span className="flex-grow-1">
                                  <strong>{a.anthem_title}</strong>
                                  {a.composer && <span className="text-muted"> — {a.composer}</span>}
                                </span>
                                <span className="text-muted ms-2" style={{ fontSize: '0.78rem', whiteSpace: 'nowrap' }}>
                                  {a.liturgy_name}
                                </span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {ideas.length === 0 && performed.length === 0 ? (
                        <div className="text-center py-4 text-muted">
                          <Music size={32} className="mb-2 opacity-50" />
                          <p className="mb-0">No ideas yet for this season</p>
                          <Button
                            variant="link"
                            size="sm"
                            onClick={() => openAddModal(season)}
                          >
                            Add the first one
                          </Button>
                        </div>
                      ) : (
                        <div className="idea-columns-container">
                          <Row>
                            {typesWithIdeas.map(type => (
                              <Col key={type.id} xs={12} md={6} lg={Math.max(4, Math.floor(12 / typesWithIdeas.length))} className="mb-3">
                                <div className="type-column">
                                  <div className="type-header d-flex justify-content-between align-items-center mb-2 pb-2 border-bottom">
                                    <h6 className="mb-0 text-muted">{type.name}</h6>
                                    <Button
                                      variant="link"
                                      size="sm"
                                      className="p-0"
                                      onClick={() => openAddModal(season, type.id)}
                                      title={`Add ${type.name}`}
                                    >
                                      <Plus size={16} />
                                    </Button>
                                  </div>

                                  <div className="type-ideas">
                                    {(ideasByType[type.id] || []).map(idea => (
                                      <div
                                        key={idea.id}
                                        className="idea-card p-2 mb-2 rounded"
                                        style={{
                                          backgroundColor: '#f8f9fa',
                                          borderLeft: `3px solid ${seasonColor}`
                                        }}
                                      >
                                        <div className="d-flex justify-content-between align-items-start">
                                          <div className="flex-grow-1 small">
                                            <strong>{idea.anthem_title}</strong>
                                            {idea.target_date && (
                                              <Badge
                                                bg="light"
                                                text="dark"
                                                className="ms-2 border"
                                                style={{ fontSize: '0.7em', fontWeight: 500 }}
                                                title="Penciled in for this Sunday"
                                              >
                                                <Calendar size={10} className="me-1" style={{ verticalAlign: '-1px' }} />
                                                {shortDate(idea.target_date)}
                                              </Badge>
                                            )}
                                            {idea.composer && (
                                              <div className="text-muted" style={{ fontSize: '0.8em' }}>
                                                {idea.composer}
                                                {idea.arranger && ` / arr. ${idea.arranger}`}
                                              </div>
                                            )}
                                            {idea.voicing && (
                                              <Badge bg="light" text="dark" className="mt-1" style={{ fontSize: '0.7em' }}>
                                                {idea.voicing}
                                              </Badge>
                                            )}
                                          </div>
                                          <div className="ms-2 d-flex gap-1">
                                            <Button
                                              variant="link"
                                              size="sm"
                                              className="p-0 text-secondary"
                                              onClick={() => openEditModal(idea)}
                                              title="Edit"
                                            >
                                              <Edit2 size={12} />
                                            </Button>
                                            <Button
                                              variant="link"
                                              size="sm"
                                              className="p-0 text-danger"
                                              onClick={() => handleDelete(idea.id, idea.anthem_title)}
                                              title="Delete"
                                            >
                                              <Trash2 size={12} />
                                            </Button>
                                          </div>
                                        </div>
                                        {idea.notes && (
                                          <div className="mt-1 fst-italic text-muted" style={{ fontSize: '0.75em' }}>
                                            {idea.notes}
                                          </div>
                                        )}
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              </Col>
                            ))}
                          </Row>
                        </div>
                      )}
                    </Card.Body>
                  )}
                </Card>
              </Col>
            );
          })}
        </Row>

        {/* Add/Edit Idea Modal — also handles reuse-from-past-years + add-for-real */}
        <Modal show={showModal} onHide={() => setShowModal(false)} centered>
          <Modal.Header closeButton>
            <Modal.Title>{editingIdea ? 'Edit Idea' : 'Add Idea'}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form>
              {/* Reuse from a past year (Add mode only) */}
              {!editingIdea && anthemHistory.length > 0 && (
                <Form.Group className="mb-3 position-relative">
                  <Form.Label className="text-muted small">Reuse a past anthem <span className="fw-normal">(optional)</span></Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Search anthems from past years…"
                    value={reuseTerm}
                    onChange={(e) => { setReuseTerm(e.target.value); setReuseOpen(true); }}
                    onFocus={() => setReuseOpen(true)}
                  />
                  {reuseOpen && reuseMatches().length > 0 && (
                    <div className="border rounded position-absolute w-100 bg-white shadow-sm" style={{ zIndex: 5, maxHeight: 220, overflowY: 'auto' }}>
                      {reuseMatches().map((item, i) => (
                        <button
                          key={`${item.anthem_title}-${i}`}
                          type="button"
                          className="d-block w-100 text-start btn btn-link text-decoration-none px-3 py-2 border-bottom"
                          onClick={() => applyReuse(item)}
                        >
                          <span className="small">
                            <strong className="text-dark">{item.anthem_title}</strong>
                            {item.composer && <span className="text-muted"> — {item.composer}</span>}
                            <span className="text-muted ms-2" style={{ fontSize: '0.85em' }}>
                              ({item.season}{item.program_year ? `, ${item.program_year}` : ''}{item.source === 'sung' ? ', sung' : ''})
                            </span>
                          </span>
                        </button>
                      ))}
                    </div>
                  )}
                  <hr className="mt-3 mb-0" />
                </Form.Group>
              )}

              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Season *</Form.Label>
                    <Form.Select
                      value={modalSeason}
                      onChange={(e) => {
                        // Dates belong to a season's Sundays — clear any penciled date on switch.
                        setModalSeason(e.target.value);
                        setFormData(prev => ({ ...prev, target_date: '' }));
                      }}
                    >
                      {seasonData.seasons.map(s => (
                        <option key={s} value={s}>{s}</option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Type *</Form.Label>
                    <Form.Select
                      value={formData.performance_type_id}
                      onChange={(e) => setFormData({ ...formData, performance_type_id: parseInt(e.target.value) })}
                    >
                      <option value={0}>Select type...</option>
                      {performanceTypes.map(pt => (
                        <option key={pt.id} value={pt.id}>{pt.name}</option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
              </Row>

              <Form.Group className="mb-3">
                <Form.Label>Title *</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="e.g., O Come, O Come Emmanuel"
                  value={formData.anthem_title}
                  onChange={(e) => setFormData({ ...formData, anthem_title: e.target.value })}
                />
              </Form.Group>

              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Composer</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="e.g., John Rutter"
                      value={formData.composer}
                      onChange={(e) => setFormData({ ...formData, composer: e.target.value })}
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Arranger</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="e.g., arr. Smith"
                      value={formData.arranger}
                      onChange={(e) => setFormData({ ...formData, arranger: e.target.value })}
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Voicing</Form.Label>
                    <Form.Select
                      value={formData.voicing}
                      onChange={(e) => setFormData({ ...formData, voicing: e.target.value })}
                    >
                      <option value="">Select voicing...</option>
                      <option value="SATB">SATB</option>
                      <option value="SAB">SAB</option>
                      <option value="SA">SA</option>
                      <option value="TB">TB</option>
                      <option value="Unison">Unison</option>
                      <option value="2-Part">2-Part</option>
                      <option value="3-Part">3-Part</option>
                      <option value="SSAA">SSAA</option>
                      <option value="TTBB">TTBB</option>
                      <option value="Instrumental">Instrumental</option>
                      <option value="Other">Other</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Date</Form.Label>
                    <Form.Select
                      value={formData.target_date}
                      onChange={(e) => setFormData({ ...formData, target_date: e.target.value })}
                    >
                      <option value="">— none —</option>
                      {sundaysForSeason(modalSeason).map(s => (
                        <option key={s.service_id} value={s.liturgy_date}>
                          {new Date(s.liturgy_date + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                          {' — '}{s.liturgy_name}
                        </option>
                      ))}
                    </Form.Select>
                    <Form.Check
                      type="checkbox"
                      id="add-for-real"
                      className="mt-2 small"
                      checked={addForReal}
                      onChange={(e) => setAddForReal(e.target.checked)}
                      label="add to planning database"
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Form.Group className="mb-3">
                <Form.Label>Notes</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={2}
                  placeholder="Any notes (difficulty, accompaniment needs, etc.)"
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                />
              </Form.Group>

              {/* When "add to planning database" is ticked, it needs a date + a
                  library song (a real song is required to become the offertory anthem). */}
              {addForReal && (
                <div className="mb-2">
                  {!formData.target_date && (
                    <div className="text-danger small mb-2">Choose a date above to add it to the planning database.</div>
                  )}
                  {pickedSong ? (
                    <div className="p-2 rounded d-flex justify-content-between align-items-center" style={{ backgroundColor: '#e8f5e9' }}>
                      <span className="small">
                        Song: <strong>{pickedSong.title}</strong>
                        {pickedSong.composer_name && <span className="text-muted"> — {pickedSong.composer_name}</span>}
                      </span>
                      <Button variant="link" size="sm" className="p-0 text-secondary" onClick={() => { setPickedSong(null); setSongTerm(''); setFormData(prev => ({ ...prev, song_id: null })); }}>
                        <X size={16} />
                      </Button>
                    </div>
                  ) : (
                    <>
                      <Form.Control
                        type="text"
                        size="sm"
                        placeholder="Pick the matching song in your library…"
                        value={songTerm}
                        onChange={(e) => searchSongs(e.target.value)}
                      />
                      {songResults.length > 0 && (
                        <div className="border rounded mt-1" style={{ maxHeight: 180, overflowY: 'auto' }}>
                          {songResults.map(s => (
                            <button
                              key={s.song_id}
                              type="button"
                              className="d-block w-100 text-start btn btn-link text-decoration-none px-3 py-2 border-bottom"
                              onClick={() => pickSong(s)}
                            >
                              <span className="small">
                                <strong className="text-dark">{s.title}</strong>
                                {s.composer_name && <span className="text-muted"> — {s.composer_name}</span>}
                              </span>
                            </button>
                          ))}
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}
            </Form>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowModal(false)}>
              <X size={16} className="me-1" />
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleSave}
              disabled={saving || !formData.anthem_title.trim() || !formData.performance_type_id || !realReady}
            >
              {saving ? (
                <>
                  <Spinner size="sm" className="me-1" />
                  Saving...
                </>
              ) : (
                <>
                  <Save size={16} className="me-1" />
                  {editingIdea ? 'Update' : 'Add'}
                </>
              )}
            </Button>
          </Modal.Footer>
        </Modal>
      </Container>

      <ConfirmModal />

      <style>{`
        .idea-card {
          transition: transform 0.15s ease, box-shadow 0.15s ease;
        }
        .idea-card:hover {
          transform: translateX(2px);
          box-shadow: 0 2px 6px rgba(0,0,0,0.08);
        }
        .type-column {
          background: #fafafa;
          padding: 12px;
          border-radius: 8px;
          min-height: 100px;
        }
        .type-header h6 {
          font-size: 0.9rem;
          font-weight: 600;
        }
        @media (max-width: 767px) {
          .type-column {
            margin-bottom: 1rem;
          }
        }
      `}</style>
    </>
  );
};

export default SeasonalAnthemPlanningPage;
